import { PageViewContext } from "@/hooks/usePageView";
import { getCanonicalUrl, normalizeCanonicalPath } from "@/lib/seo";
import { appConfig } from "@services/AppConfig";
import { ReactNode, useCallback, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router-dom";

export default function RouteTracker({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const lastPage = useRef<string>();

  const reportPageView = useCallback(
    ({ pathname, search }: { pathname: string; search: string }) => {
      const page = normalizeCanonicalPath(pathname);
      // Share deduplication across page mounts and StrictMode. Query parameters
      // describe attribution/UI state, not a separate page in the current router.
      if (
        appConfig.googleAnalyticsId &&
        appConfig.stage !== "local" &&
        lastPage.current !== page
      ) {
        ReactGA.send({
          hitType: "pageview",
          page,
          // react-ga4 maps `location` to GA4 page_location.
          location: new URL(pathname + search, window.location.origin).href,
        });
        lastPage.current = page;
      }
    },
    [],
  );

  useEffect(() => {
    // Campaign query strings must not fragment the canonical URL.
    let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", "canonical");
      document.head.appendChild(el);
    }
    el.setAttribute("href", getCanonicalUrl(pathname));
  }, [pathname]);

  return (
    <PageViewContext.Provider value={reportPageView}>{children}</PageViewContext.Provider>
  );
}
